import { useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { postLoginPath } from "../utils/postLoginRedirect";
import { AppFooter } from "../components/AppFooter";

const indirammaLogoSrc = new URL("../assets/indiramma_indlu_logo1.png", import.meta.url).href;
const telanganaLogoSrc = new URL("../assets/branding/Telangana-LOGO.png", import.meta.url).href;
const telanganaRisingLogoSrc = new URL("../../public/branding/rising_telangana.png", import.meta.url).href;
const indirammaBannerSrc = new URL("../../public/branding/indiramma_3d.jpeg", import.meta.url).href;

export function LoginPage() {
  const { authRequired, user, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from ?? "/";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (!authRequired) {
    return <Navigate to="/" replace />;
  }

  if (user) {
    return <Navigate to={postLoginPath(from === "/login" ? "/" : from, user)} replace />;
  }

  return (
    <div className="portal-login">
      <a href="#portal-main" className="skip-link">
        Skip to main content
      </a>

      <div className="portal-login__hero">
        <div className="portal-login__hero-media" aria-hidden="true">
          <img
            src={indirammaBannerSrc}
            alt=""
            className="portal-login__hero-img"
            width={1600}
            height={900}
            loading="eager"
            decoding="async"
          />
          <div className="portal-login__hero-scrim" />
        </div>

        <header className="portal-login__header portal-login__header--over-hero">
          <div className="portal-login__header-inner">
            <div className="portal-login__header-left">
              <img
                src={indirammaLogoSrc}
                alt="Indiramma Indlu — Government of Telangana, Chief Minister’s Office"
                className="portal-login__indiramma-logo"
              />
            </div>
            <div className="portal-login__header-right">
              <img
                src={telanganaLogoSrc}
                alt="Government of Telangana — state emblem"
                className="portal-login__telangana-emblem"
                width={64}
                height={64}
              />
              <img
                src={telanganaRisingLogoSrc}
                alt="Telangana Rising — CURE · PURE · RARE"
                className="portal-login__telangana-rising"
                width={120}
                height={120}
              />
            </div>
          </div>
        </header>

        <main className="portal-login__hero-inner" id="portal-main">
            <div className="portal-login__hero-copy">
              <h1 className="visually-hidden">Indiramma Indlu — Head Office console</h1>
            </div>

            <div className="portal-login__hero-aside">
              <div className="portal-login__shell">
                <section className="portal-login__panel" aria-labelledby="portal-panel-title">
                  <div className="portal-login__panel-head">
                    <h2 id="portal-panel-title" className="portal-login__panel-title">
                      Sign in
                    </h2>
                  </div>

                  <div className="portal-login__panel-body">
                    <form
                      className="portal-login__form"
                      onSubmit={(e) => {
                        e.preventDefault();
                        setErr(null);
                        setBusy(true);
                        void (async () => {
                          try {
                            const signedIn = await login(email.trim(), password);
                            navigate(postLoginPath(from === "/login" ? "/" : from, signedIn), { replace: true });
                          } catch (ex) {
                            setErr(ex instanceof Error ? ex.message : String(ex));
                          } finally {
                            setBusy(false);
                          }
                        })();
                      }}
                    >
                      <div className="portal-login__field">
                        <label htmlFor="login-email" className="portal-login__label">
                          Work email
                        </label>
                        <input
                          id="login-email"
                          className="portal-login__input"
                          type="email"
                          autoComplete="username"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          required
                          placeholder="name@government.example"
                        />
                      </div>
                      <div className="portal-login__field">
                        <label htmlFor="login-password" className="portal-login__label">
                          Password
                        </label>
                        <input
                          id="login-password"
                          className="portal-login__input"
                          type="password"
                          autoComplete="current-password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          required
                          placeholder="••••••••"
                        />
                      </div>
                      {err ? (
                        <p className="portal-login__error" role="alert">
                          {err}
                        </p>
                      ) : null}
                      <button type="submit" className="portal-login__search-btn" disabled={busy}>
                        {busy ? "Signing in…" : "Sign in"}
                      </button>
                    </form>
                    <p className="portal-login__hint">
                      Trouble signing in? Contact your programme <strong>superadmin</strong> or IT support.
                    </p>
                  </div>
                </section>
              </div>
            </div>
      </main>

        <blockquote className="portal-login__quote-card" lang="te">
          <span className="portal-login__quote-card__mark portal-login__quote-card__mark--open" aria-hidden="true">
            “
          </span>
          <div className="portal-login__quote-card__lines">
            <p>
              <span className="portal-login__quote-card__row">అర్హులైన కుటుంబాలకు సొంత స్థలాల్లో</span>
              <span className="portal-login__quote-card__row">సొంతంగా కొత్త ఇల్లు నిర్మించుకునేందుకు ఆర్థిక</span>
              <span className="portal-login__quote-card__row">సహాయం అందించడం</span>
            </p>
          </div>
          <span className="portal-login__quote-card__mark portal-login__quote-card__mark--close" aria-hidden="true">
            ”
          </span>
        </blockquote>
      </div>

      <AppFooter variant="portal" />
    </div>
  );
}
